import { useEffect, useMemo, useState } from "react";
import {
  Cell, Pie, PieChart, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, LabelList, LineChart, Line, Legend,
} from "recharts";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Download, FileDown, TrendingUp, CheckCircle2, RefreshCcw, Flag, Loader2 } from "lucide-react";
import { api, downloadFile, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProject } from "@/context/ProjectContext";
import { LOGOS, MILESTONE_BADGE, ROLE_LABEL, ROLE_LOGO, STATO_COLORS, fmtDate } from "@/lib/constants";
import { PmBot } from "@/components/PmBot";

const KpiCard = ({ label, value, sub, icon: Icon, testid }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid={testid}>
    <div className="flex items-start justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">{label}</p>
      <Icon className="h-4 w-4 text-slate-400 shrink-0" />
    </div>
    <p className="mt-3 text-3xl font-bold tracking-tight text-slate-900">{value}</p>
    <p className="mt-1 text-sm text-slate-500">{sub}</p>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const { projectId } = useProject();
  const [data, setData] = useState(null);
  const [sprint, setSprint] = useState("");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!projectId) return;
    setData(null);
    api
      .get(`/projects/${projectId}/dashboard`)
      .then(async ({ data: base }) => {
        const active = base.sprints.find((x) => x.percentuale > 0 && x.percentuale < 100) || base.sprints[0];
        const key = active?.key || "";
        setSprint(key);
        if (key) {
          const { data: full } = await api.get(`/projects/${projectId}/dashboard`, { params: { sprint: key } });
          setData(full);
        } else setData(base);
      })
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [projectId]);

  const onSprintChange = async (v) => {
    setSprint(v);
    const { data: full } = await api.get(`/projects/${projectId}/dashboard`, { params: { sprint: v } });
    setData(full);
  };

  const exportExcel = async () => {
    setBusy("xlsx");
    try {
      await downloadFile(`/projects/${projectId}/export/excel`, "Piano_aggiornato.xlsx");
      toast.success("Excel aggiornato scaricato");
    } catch (e) {
      toast.error("Errore nel download Excel");
    }
    setBusy("");
  };

  const exportPdf = async () => {
    setBusy("pdf");
    try {
      await downloadFile(`/projects/${projectId}/export/pdf?sprint=${encodeURIComponent(sprint)}`, "SprintReview.pdf");
      toast.success("PDF Sprint Review generato");
    } catch (e) {
      toast.error("Errore nella generazione del PDF");
    }
    setBusy("");
  };

  const stats = data?.sprint_corrente || data?.globale;
  const pieData = useMemo(() => (stats?.distribuzione || []).filter((d) => d.value > 0), [stats]);

  if (!data || !stats)
    return (
      <div className="h-64 grid place-items-center text-slate-400" data-testid="dashboard-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );

  const g = data.globale;
  const isCliente = user.role === "cliente_finale";
  const isBcnext = user.role === "fornitore";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-4">
          <img src={ROLE_LOGO[user.role] || LOGOS.governance} alt="" className="h-12 w-12 rounded-xl object-cover border border-slate-200 bg-white" />
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">
              {data.project.nome} · {ROLE_LABEL[user.role]}
            </p>
            <h1 className="mt-1 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              {isCliente ? "Avanzamento Progetto" : "Dashboard Governance"}
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              {isBcnext
                ? "Vedi e aggiorni solo i task in carico a Stefano Critelli: le tue percentuali alimentano il consolidamento Majorbit."
                : isCliente
                ? "Percentuali ufficiali consolidate da Majorbit, aggiornate a partire dagli avanzamenti Stefano Critelli."
                : "Stefano Critelli dichiara, Majorbit consolida, il cliente vede le percentuali consolidate."}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={sprint} onValueChange={onSprintChange}>
            <SelectTrigger className="w-[200px] h-10 bg-white" data-testid="dashboard-sprint-select">
              <SelectValue placeholder="Sprint" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {data.sprints.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`sprint-option-${s.key.replace(/\s/g, "")}`}>
                  {s.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={exportExcel} disabled={busy === "xlsx"} variant="outline"
                  className="h-10 bg-white border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
                  data-testid="export-excel-button">
            {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Excel
          </Button>
          <Button onClick={exportPdf} disabled={busy === "pdf" || !sprint}
                  className="h-10 bg-slate-900 text-white hover:bg-slate-800 hover:text-white transition-colors"
                  data-testid="export-pdf-button">
            {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDown className="h-4 w-4 mr-2" />}
            PDF Sprint Review
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label={isBcnext ? "Avanzamento Stefano Critelli" : "Avanzamento consolidato"}
                 value={`${isBcnext ? stats.percentuale_fornitore : stats.sprint_percentuale}%`}
                 sub={`${stats.giorni_completati} di ${stats.giorni_totali} gg · sprint`} icon={TrendingUp}
                 testid="kpi-average-progress" />
        <KpiCard label="Task completati" value={`${stats.completati}/${stats.totali}`}
                 sub={`${stats.in_corso} in corso · ${stats.in_test} in test`} icon={CheckCircle2}
                 testid="kpi-completed-tasks" />
        <KpiCard label="Rework tester" value={stats.rework_totale}
                 sub={`${stats.ko} task KO · indice ${stats.rework_rate}%`} icon={RefreshCcw}
                 testid="kpi-rework-rate" />
        <KpiCard label="Avanzamento progetto" value={`${g.sprint_percentuale}%`}
                 sub={`${g.completati}/${g.totali} task totali`} icon={Flag}
                 testid="kpi-project-progress" />
      </div>

      {!isCliente && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-center gap-4"
               data-testid="chain-fornitore-card">
            <img src={LOGOS.fornitore} alt="Stefano Critelli" className="h-11 w-11 rounded-lg object-cover" />
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-indigo-500 font-mono">
                Dichiarato da Stefano Critelli
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-slate-900" data-testid="chain-fornitore-value">
                  {stats.percentuale_fornitore}%
                </p>
                <span className="text-xs text-slate-500">{stats.task_fornitore} task</span>
              </div>
              <Progress value={stats.percentuale_fornitore} className="h-1.5 mt-2" />
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm flex items-center gap-4"
               data-testid="chain-cliente-card">
            <img src={LOGOS.cliente} alt="Majorbit" className="h-11 w-11 rounded-lg object-cover" />
            <div className="flex-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 font-mono">
                Consolidato da Majorbit (visto dal cliente)
              </p>
              <div className="flex items-baseline gap-2">
                <p className="text-2xl font-bold text-slate-900" data-testid="chain-cliente-value">
                  {stats.sprint_percentuale}%
                </p>
                <span className="text-xs text-slate-500">{stats.totali} task</span>
              </div>
              <Progress value={stats.sprint_percentuale} className="h-1.5 mt-2" />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-6 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="pie-chart-card">
          <h3 className="text-lg font-semibold text-slate-900">Distribuzione stati task</h3>
          <p className="text-sm text-slate-500">Sprint selezionato</p>
          <div className="h-[280px] mt-4">
            {pieData.length === 0 ? (
              <p className="text-sm text-slate-400 pt-16 text-center">Nessun task nello sprint selezionato</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={62} outerRadius={104}
                       paddingAngle={2} label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                    {pieData.map((d) => (<Cell key={d.name} fill={STATO_COLORS[d.name]} stroke="#fff" strokeWidth={2} />))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff" }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="lg:col-span-6 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="rework-chart-card">
          <h3 className="text-lg font-semibold text-slate-900">Rework per tester</h3>
          <p className="text-sm text-slate-500">Interventi di rilavorazione registrati</p>
          <div className="h-[280px] mt-4">
            {(stats.rework_per_tester || []).length === 0 ? (
              <p className="text-sm text-slate-400 pt-16 text-center">Nessun rework registrato</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.rework_per_tester} margin={{ top: 16, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="tester" tick={{ fontSize: 12, fill: "#64748B" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff" }} />
                  <Bar dataKey="rework" fill="#EF4444" radius={[6, 6, 0, 0]} barSize={38}>
                    <LabelList dataKey="rework" position="top" fontSize={12} fill="#0F172A" />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="lg:col-span-12 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="storico-card">
          <h3 className="text-lg font-semibold text-slate-900">Storico avanzamenti e rework</h3>
          <p className="text-sm text-slate-500">
            Snapshot registrato automaticamente ad ogni aggiornamento delle percentuali
          </p>
          <div className="h-[260px] mt-4">
            {(data.storico || []).length < 2 ? (
              <p className="text-sm text-slate-400 pt-20 text-center" data-testid="storico-empty">
                Servono almeno due giorni di aggiornamenti per disegnare il trend. Snapshot registrati:{" "}
                {(data.storico || []).length}.
              </p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data.storico} margin={{ top: 8, right: 16, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                  <XAxis dataKey="data" tickFormatter={(v) => v.slice(5)} tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", background: "#fff" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="percentuale_fornitore" name="% Stefano Critelli" stroke="#4F46E5" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="percentuale_cliente" name="% Majorbit" stroke="#F59E0B" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="rework_totale" name="Rework cumulati" stroke="#EF4444" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="lg:col-span-7 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="sprint-progress-card">
          <h3 className="text-lg font-semibold text-slate-900">Avanzamento per sprint</h3>
          <div className="mt-5 space-y-4">
            {data.sprints.map((s) => (
              <div key={s.key}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="font-medium text-slate-700">{s.nome}</span>
                  <span className="font-mono text-slate-900 font-semibold">
                    {!isCliente && <span className="text-indigo-600 mr-2">BCN {s.percentuale_fornitore}%</span>}
                    {s.percentuale}%
                  </span>
                </div>
                <Progress value={s.percentuale} className="h-2" />
                <p className="mt-1 text-xs text-slate-400">{fmtDate(s.data_inizio)} → {fmtDate(s.data_fine)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="milestone-health-card">
          <h3 className="text-lg font-semibold text-slate-900">Salute milestone</h3>
          <div className="mt-5 space-y-3">
            {data.milestones.map((m) => (
              <div key={m.codice} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-3 last:border-0">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-800 truncate">{m.nome}</p>
                  <p className="text-xs text-slate-400">{fmtDate(m.data)}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-mono font-semibold text-slate-900">{m.percentuale}%</p>
                  <span className={`inline-block text-[10px] font-mono px-2 py-0.5 rounded-full border ${MILESTONE_BADGE[m.stato]}`}>
                    {m.stato}
                  </span>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400 font-mono">Avanzamento per area</h4>
            <div className="mt-3 space-y-2">
              {data.avanzamento_per_area.map((a) => (
                <div key={a.area} className="flex items-center gap-3">
                  <span className="font-mono text-xs w-8 text-slate-600">{a.area}</span>
                  <Progress value={a.percentuale} className="h-1.5 flex-1" />
                  <span className="font-mono text-xs text-slate-900 w-10 text-right">{a.percentuale}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <PmBot pagina="dashboard" sprint={sprint || null} />

    </div>
  );
}
